import React, { useCallback, useEffect, useState } from "react";
import "./TenantPaymentHistory.css";
import Modal from "components/common/Modal/Modal";
import { TenantRentService } from "services/tenant-rent-payment";
import Loader from "components/Loader/Loader";
import { formatDate, getTodaysDate } from "ultils/date-formatter";
import TenantRentPaymentForm from "components/features/TenantPaymentForm/TenantPaymentForm";
import TenantRentPaymentFormInterface from "Interfaces/TenantRentPaymentForm.model";
import TenantPaymentStatus from "components/features/TenantRentPaymentStatus/TenantRentPaymentStatus";
import { formatCurrency } from "ultils/currency-formatter";
import { useNavigate } from "react-router-dom";
import { getOrdinalSuffix } from "ultils/ordinal";

const TenantPaymentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error] = useState<string | null>(null);
  const [tenantPaymentHistory, setTenantPaymentHistory] = useState<any>([]);
  const [ogTenantPaymentHistory, setOGTenantPaymentHistory] = useState<any>([]);
  const [initialPaymentFormValues, setInitialPaymentFormValues] =
    useState<TenantRentPaymentFormInterface>();

  const [selectedFilter, setSelectedFilter] = useState<string>("all");

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const loadATenantPaymentHistory = useCallback(async () => {
    try {
      const data = await TenantRentService.fetchTenantPaymentHistory();
      console.log(data);

      // Assuming the structure matches your API response
      const updatedTenants = data.paymentHistory.map((tenant: any) => {
        // Call the function for each tenant
        return checkIfHasPaidCurrentMonth(tenant);
      });

      setSelectedFilter("all");
      console.log("updated tenants", updatedTenants);
      setTenantPaymentHistory(updatedTenants);
      setOGTenantPaymentHistory(updatedTenants);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching property details:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadATenantPaymentHistory();
  }, [loadATenantPaymentHistory]);

  const postTenantPaidRentHistory = async (rentPayment: any) => {
    console.log("Property Details Submitted:", rentPayment);
    try {
      await TenantRentService.addRentPaymentFromTenant(rentPayment);
      closeModal();
      loadATenantPaymentHistory();
    } catch (err) {
      alert("Failed to update property");
    }
  };

  const tenantPaidBtnClicked = async (tenant: any, paidStatus: string) => {
    console.log(tenant);
    console.log(paidStatus);

    setInitialPaymentFormValues({
      tenantId: tenant.tenantId,
      propertyId: tenant.property.propertyId,
      actualRent: tenant.property.isMultiFamily
        ? tenant.unit.actualRent
        : tenant.property.actualRent,
      amountPaid:
        paidStatus === "full"
          ? tenant.unit?.actualRent ?? tenant.property.actualRent
          : 0,
      Id: tenant.Id,
      paymentDate: getTodaysDate(),
      partialPayment: paidStatus === "full" ? false : true,
      amountDue: paidStatus === "full" ? 0 : tenant.property.actualRent,
      monthPaid: getCurrentMonth(),
    });

    openModal();
  };

  const calculateTotalYTD = () => {
    // if (tenantPaymentHistory.length === 0) return 0;

    // const totalAmountPaid = tenantPaymentHistory.reduce(
    //   (total: any, rent: any) => {
    //     const paymentSum = rent.paymentHistory.reduce(
    //       (sum: any, payment: any) => sum + payment.amountPaid,
    //       0
    //     );
    //     return total + paymentSum;
    //   },
    //   0
    // );

    // return totalAmountPaid;
    if (tenantPaymentHistory.length === 0) return 0;

    const currentYear = new Date().getFullYear();

    const totalAmountPaid = ogTenantPaymentHistory.reduce(
      (total: number, rent: any) => {
        const paymentSum = rent.paymentHistory
          .filter((payment: any) => {
            const paymentYear = new Date(payment.paymentDate).getFullYear();
            return paymentYear === currentYear; // Only include payments from the current year
          })
          .reduce((sum: number, payment: any) => sum + payment.amountPaid, 0); // Sum amounts from filtered payments

        return total + paymentSum;
      },
      0
    );

    return totalAmountPaid;
  };

  const calculateYTD = (tenant: any) => {
    const currentYear = new Date().getFullYear();

    // Filter payments that are from the current year
    const paymentsThisYear = tenant.paymentHistory.filter((payment: any) => {
      const paymentYear = new Date(payment.paymentDate).getFullYear();
      return paymentYear === currentYear;
    });

    // Sum the `amountPaid` for filtered payments
    const totalAmountPaid = paymentsThisYear.reduce(
      (sum: number, payment: any) => sum + payment.amountPaid,
      0
    );

    // Assign the calculated YTD to the tenant
    tenant.ytd = totalAmountPaid;

    return totalAmountPaid;
  };

  const getCurrentMonth = () => {
    return new Date().toLocaleString("default", {
      month: "long",
    });
  };

  const checkIfHasPaidCurrentMonth = (tenant: any) => {
    // Get the current month and year
    const currentMonth = new Date().toLocaleString("default", {
      month: "long",
    });
    const currentYear = new Date().getFullYear();

    // Initialize variables for latest payment details
    let hasPaid = false;
    let latestPaymentDate: any = null;
    let latestAmountPaid = 0;

    if (tenant.paymentHistory.length > 0) {
      tenant.paymentHistory.forEach((payment: any) => {
        const paymentDate = new Date(payment.paymentDate);
        const paymentMonth = paymentDate.toLocaleString("default", {
          month: "long",
        });
        const paymentYear = paymentDate.getFullYear();
        // Check if the payment matches the current month and year
        if (paymentMonth === currentMonth && paymentYear === currentYear) {
          hasPaid = true;
        }

        // Find the latest payment date
        if (!latestPaymentDate || paymentDate > new Date(latestPaymentDate)) {
          latestPaymentDate = payment.paymentDate; // Store as string
          latestAmountPaid = payment.amountPaid;
        }
      });
    }

    tenant.hasPaid = hasPaid;
    tenant.latestPaymentDate = latestPaymentDate;
    tenant.latestAmountPaid = latestAmountPaid;

    return tenant;
  };

  const getPaymentStatusLabel = (tenant: any) => {
    const actualRent =
      tenant.unit?.actualRent ?? tenant.property?.actualRent ?? 0;

    if (tenant.totalPaymentsThisMonth >= actualRent) {
      // debugger;
      return <span className="status-badge paid">Paid</span>;
    } else if (tenant.totalPaymentsThisMonth > 0) {
      return (
        <span className="status-badge partial-payment">Partial Payment</span>
      );
    } else {
      return <span className="status-badge not-paid">Not Paid</span>;
    }
  };

  // Handle filter change update
  const handleFilterClick = (filterType: string) => {
    setSelectedFilter(filterType);

    if (filterType === "all") {
      setTenantPaymentHistory(ogTenantPaymentHistory);
    } else {
      const isPaid = filterType === "true"; // Filters tenants who have paid or not
      const filtered = ogTenantPaymentHistory.filter(
        (tenant: any) => tenant.hasPaid === isPaid
      );
      setTenantPaymentHistory(filtered);
    }
  };

  return (
    <div className="bg">
      <h1 className="dashboard-header">Rent Management</h1>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h3>We are in {getCurrentMonth()}</h3>{" "}
        </div>
        <h3>YTD: Portfolio Revenue {formatCurrency(calculateTotalYTD())}</h3>
      </div>
      {error ? <div>Error: {error}</div> : ""}
      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="filters">
            {/* <p>Filters</p> */}
            <button
              className={`btn ${selectedFilter === "true" ? "active" : ""}`}
              onClick={() => handleFilterClick("true")}
            >
              Paid
            </button>
            <button
              className={`btn ${selectedFilter === "false" ? "active" : ""}`}
              onClick={() => handleFilterClick("false")}
            >
              Not Paid
            </button>
            <button
              className={`btn ${selectedFilter === "all" ? "active" : ""}`}
              onClick={() => handleFilterClick("all")}
            >
              All
            </button>

            <p>
              {tenantPaymentHistory.length +
                " out of " +
                ogTenantPaymentHistory.length}{" "}
              tenants
            </p>
          </div>
          <div
            className="tiles-container"
            style={{
              gap: "8.5rem",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            }}
          >
            {tenantPaymentHistory.map((tile: any, index: any) => (
              <div key={index} className="tile">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h2>{tile.fullName}</h2>
                  {getPaymentStatusLabel(tile)}
                </div>
                <p>
                  Total Payments this month:{" "}
                  <b>{formatCurrency(tile.totalPaymentsThisMonth)}</b>
                </p>
                <p>
                  Last payment of{" "}
                  <b>
                    {formatCurrency(tile.latestAmountPaid) +
                      " on " +
                      formatDate(tile.latestPaymentDate, "MMMM dd, yyyy")}
                  </b>
                  <br />
                  YTD: <b>{formatCurrency(calculateYTD(tile))}</b> - Rent Per
                  Month:{" "}
                  <b>
                    {" "}
                    {formatCurrency(
                      tile.unit === null
                        ? tile.property.actualRent
                        : tile.unit.actualRent
                    )}
                  </b>{" "}
                  <br />
                  Due on every <b>{getOrdinalSuffix(tile.rentDueDay)}</b> of the
                  month
                </p>
                <p
                  style={{ textDecoration: "underline" }}
                  onClick={() =>
                    navigate(
                      `/main/property-dashboard/${tile.property.propertyId}`
                    )
                  }
                >
                  {tile.unit === null
                    ? tile.property.fullAddress
                    : tile.unit.fullAddress}
                </p>
                <p>
                  Lease Signed on{" "}
                  {formatDate(tile.leaseStartDate, "MMMM dd, yyyy")}
                </p>
                <TenantPaymentStatus paymentHistory={tile.paymentHistory} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginTop: "20px",
                  }}
                >
                  <button
                    className="btn"
                    onClick={() => tenantPaidBtnClicked(tile, "full")}
                  >
                    Paid in Full this Month
                  </button>
                  <button
                    className="btn"
                    onClick={() => tenantPaidBtnClicked(tile, "partial")}
                  >
                    Partial Payment this Month
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Modal
        title={"Tenant Rent Payment"}
        isOpen={isModalOpen}
        onClose={closeModal}
      >
        <TenantRentPaymentForm
          initialFormValues={initialPaymentFormValues}
          postTenantPaidRentHistory={postTenantPaidRentHistory}
        ></TenantRentPaymentForm>
      </Modal>
      {!loading && tenantPaymentHistory.length === 0 ? (
        <div>
          <p>Add a Property and a Tenant to get started.</p>
        </div>
      ) : null}
    </div>
  );
};

export default TenantPaymentDashboard;
